import { useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '../store';
import {
  setStatus,
  setSessionInfo,
  setPhase,
  addMessage,
  setCurrentScore,
  setTeacherSpeaking,
  setPlayback,
  setProcessingStep,
  setSummary,
  setUsage,
  setError,
} from '../store/slices/liveSessionSlice';

let _getToken: (() => Promise<string | null>) | null = null;

export function setLiveSessionGetToken(fn: () => Promise<string | null>) {
  _getToken = fn;
}

function b64ToAudioUrl(b64: string): string | undefined {
  if (!b64) return undefined;
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  const blob = new Blob([bytes], { type: 'audio/mpeg' });
  return URL.createObjectURL(blob);
}

/**
 * Audio queue — plays TTS clips sequentially so messages don't stomp each other.
 * Each item is { audioUrl, onStart, onEnd }.
 */
interface QueueItem {
  audioUrl: string;
  onStart: () => void;
  onEnd: () => void;
  onTimeUpdate?: (timeMs: number) => void;
}

function createAudioQueue() {
  const queue: QueueItem[] = [];
  let playing = false;
  let animFrame = 0;
  const audio = new Audio();

  function trackTime(item: QueueItem) {
    if (!audio.paused && item.onTimeUpdate) {
      item.onTimeUpdate(audio.currentTime * 1000);
      animFrame = requestAnimationFrame(() => trackTime(item));
    }
  }

  function playNext() {
    cancelAnimationFrame(animFrame);
    if (queue.length === 0) {
      playing = false;
      return;
    }
    playing = true;
    const item = queue.shift()!;
    item.onStart();
    audio.src = item.audioUrl;
    audio.onended = () => {
      cancelAnimationFrame(animFrame);
      item.onEnd();
      playNext();
    };
    audio.onerror = () => {
      cancelAnimationFrame(animFrame);
      item.onEnd();
      playNext();
    };
    audio.play().then(() => {
      if (item.onTimeUpdate) {
        animFrame = requestAnimationFrame(() => trackTime(item));
      }
    }).catch(() => {
      item.onEnd();
      playNext();
    });
  }

  return {
    enqueue(item: QueueItem) {
      queue.push(item);
      if (!playing) playNext();
    },
    clear() {
      cancelAnimationFrame(animFrame);
      queue.length = 0;
      audio.pause();
      audio.src = '';
      playing = false;
    },
  };
}

export function useLiveSession(onPlayReference?: () => void) {
  const wsRef = useRef<WebSocket | null>(null);
  const audioQueueRef = useRef(createAudioQueue());
  const onPlayReferenceRef = useRef(onPlayReference);
  onPlayReferenceRef.current = onPlayReference;
  const messageCountRef = useRef(0);
  const dispatch = useDispatch<AppDispatch>();

  const connect = useCallback(async (sessionId: string) => {
    dispatch(setStatus('connecting'));

    // Clear any leftover audio from previous session
    audioQueueRef.current.clear();

    let token = '';
    if (_getToken) {
      try {
        token = (await _getToken()) || '';
      } catch { /* no auth */ }
    }

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    const wsUrl = `${protocol}//${host}/api/live-session/${sessionId}?token=${token}`;

    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      dispatch(setStatus('active'));
    };

    ws.onmessage = (event) => {
      const msg = JSON.parse(event.data);

      switch (msg.type) {
        case 'session_started':
          dispatch(setSessionInfo({
            sessionId: msg.session_id,
            surah: msg.surah,
            ayah: msg.ayah,
            surahName: msg.surah_name,
            ayahText: msg.ayah_text,
          }));
          break;

        case 'teacher_message': {
          const audioUrl = b64ToAudioUrl(msg.audio_b64);
          const shouldPlayRef = !!msg.play_reference;
          const hasTimedSegs = msg.timed_segments && msg.timed_segments.length > 0;

          dispatch(addMessage({
            role: 'teacher',
            text: msg.text,
            audioUrl,
            playReference: shouldPlayRef,
            timedSegments: msg.timed_segments || undefined,
            timestamp: Date.now(),
          }));
          const msgIndex = messageCountRef.current;
          messageCountRef.current += 1;
          dispatch(setPhase(msg.phase as any));

          if (audioUrl) {

            audioQueueRef.current.enqueue({
              audioUrl,
              onStart: () => dispatch(setTeacherSpeaking(true)),
              onEnd: () => {
                dispatch(setTeacherSpeaking(false));
                dispatch(setPlayback(null));
                if (shouldPlayRef && onPlayReferenceRef.current) {
                  onPlayReferenceRef.current();
                }
              },
              onTimeUpdate: hasTimedSegs
                ? (timeMs: number) => dispatch(setPlayback({ messageIndex: msgIndex, timeMs }))
                : undefined,
            });
          } else if (shouldPlayRef && onPlayReferenceRef.current) {
            onPlayReferenceRef.current();
          }
          break;
        }

        case 'listening':
          dispatch(setPhase('listening'));
          dispatch(setProcessingStep(null));
          break;

        case 'processing':
          dispatch(setPhase('processing'));
          dispatch(setProcessingStep(msg.step));
          break;

        case 'turn_result':
          dispatch(setCurrentScore(msg.score));
          if (msg.usage) {
            dispatch(setUsage(msg.usage));
          }
          dispatch(addMessage({
            role: 'student',
            text: msg.transcript || 'Recitation submitted',
            score: msg.score,
            findings: msg.findings || [],
            timestamp: Date.now(),
          }));
          messageCountRef.current += 1;
          break;

        case 'session_ended':
          dispatch(setSummary({
            totalTurns: msg.summary.total_turns,
            averageScore: msg.summary.average_score,
            rulesPracticed: msg.summary.rules_practiced,
            durationS: msg.summary.duration_s,
          }));
          dispatch(setStatus('ended'));
          break;

        case 'error':
          dispatch(setError(msg.message));
          if (msg.code === 'usage_limit') {
            dispatch(setStatus('ended'));
          }
          break;
      }
    };

    ws.onclose = () => {
      audioQueueRef.current.clear();
      dispatch((d, getState) => {
        const state = (getState() as any).liveSession;
        if (state.status !== 'ended') {
          d(setStatus('idle'));
        }
      });
    };

    ws.onerror = () => {
      dispatch(setError('Connection error'));
      dispatch(setStatus('idle'));
    };

    wsRef.current = ws;
  }, [dispatch]);

  const send = useCallback((msg: object) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(msg));
    }
  }, []);

  const disconnect = useCallback(() => {
    audioQueueRef.current.clear();
    wsRef.current?.close();
    wsRef.current = null;
  }, []);

  return { connect, send, disconnect };
}
